import type { Variables } from '@urql/exchange-graphcache';
import { stringifyVariables } from 'urql';
import isCursorArgumentSimilar from './isCursorArgumentSimilar';

const IGNORED_FIELD_ARGUMENTS = new Set(['limit', 'order_by', 'where']);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const areValuesEqual = (firstValue: unknown, secondValue: unknown) =>
  stringifyVariables({ value: firstValue }) ===
  stringifyVariables({ value: secondValue });

function haveSameEntries(
  firstRecord: Record<string, unknown>,
  secondRecord: Record<string, unknown>,
  ignoredKeys: ReadonlySet<string>,
) {
  const firstKeys = Object.keys(firstRecord).filter(
    (key) => !ignoredKeys.has(key),
  );
  const secondKeys = Object.keys(secondRecord).filter(
    (key) => !ignoredKeys.has(key),
  );

  if (firstKeys.length !== secondKeys.length) {
    return false;
  }

  return firstKeys.every(
    (key) =>
      key in secondRecord &&
      areValuesEqual(firstRecord[key], secondRecord[key]),
  );
}

function haveSimilarWhereArguments(
  firstWhere: unknown,
  secondWhere: unknown,
  cursorArgument: string,
  cursorExclusivePredicateArgument?: string,
) {
  if (!isRecord(firstWhere) || !isRecord(secondWhere)) {
    return areValuesEqual(firstWhere, secondWhere);
  }

  const ignoredKeys = new Set<string>();
  if (cursorExclusivePredicateArgument) {
    ignoredKeys.add(cursorExclusivePredicateArgument);
  }

  const firstKeys = Object.keys(firstWhere).filter(
    (key) => !ignoredKeys.has(key),
  );
  const secondKeys = Object.keys(secondWhere).filter(
    (key) => !ignoredKeys.has(key),
  );

  if (firstKeys.length !== secondKeys.length) {
    return false;
  }

  return firstKeys.every((key) => {
    if (!(key in secondWhere)) {
      return false;
    }

    if (
      key === cursorArgument &&
      isCursorArgumentSimilar(firstWhere[key], secondWhere[key])
    ) {
      return true;
    }

    return areValuesEqual(firstWhere[key], secondWhere[key]);
  });
}

export default function isArgsSimilar(
  fieldArgs: Variables,
  connectionArgs: Variables,
  cursorArgument: string,
  cursorExclusivePredicateArgument?: string,
): boolean {
  return (
    haveSameEntries(fieldArgs, connectionArgs, IGNORED_FIELD_ARGUMENTS) &&
    haveSimilarWhereArguments(
      fieldArgs.where,
      connectionArgs.where,
      cursorArgument,
      cursorExclusivePredicateArgument,
    )
  );
}
